/**
 * แค็ตตาล็อกช่องของหน้า /dashboard — ADR 0363 · ปรับตาม mockup ใหม่ที่ ADR 0365
 * แผน:   `4-Archives/plan/tms/0365-dashboard-match-the-mockup.md`
 * สัญญาหน้าตา: `4-Archives/plan/tms/0363-dashboard-demo.html`
 *
 * แหล่งความจริงเดียวของ "หน้านี้มีช่องอะไร อยู่แถบไหน ลำดับที่เท่าไร ถามว่าอะไร วาดเป็นรูปอะไร"
 *
 * ## ลำดับในไฟล์นี้ **คือ** ลำดับบนหน้าจอ
 * `dashboard-parity.e2e.ts` เทียบลำดับนี้กับ `tests/e2e/web/fixtures/dashboard-layout.expected.json`
 * ซึ่งถอดมาจาก demo อัตโนมัติ — สลับลำดับที่นี่แล้วหน้าจอสลับตาม และเทสจะจับได้ทันทีถ้าไม่ตรง mockup
 *
 * ⚠️ ไฟล์นี้ **ไม่มีสูตรคำนวณ** — มีแต่คีย์ หน่วย รูป และปลายทางของลิงก์
 *    ตัวเลขทุกตัวมาจาก service เจ้าของโดเมนเสมอ (0363 D4b) ดู `source` ว่าใครเป็นเจ้าของ
 *
 * ⚠️ API โหลดไฟล์นี้ตอน runtime **ไม่ได้** (`main` ชี้ `src/index.ts` ที่ Node อ่านไม่ออก —
 *    เคยทำ dev server ล่มมาแล้ว) ฝั่งเซิร์ฟเวอร์มีสำเนาคีย์ของตัวเองที่
 *    `apps/api/src/modules/analytics/dashboard-tiles.server.ts` และ `dashboard-tiles-parity.spec.ts`
 *    ตรึงว่าสองฝั่งตรงกัน
 */

/* ─── แท็บ ─────────────────────────────────────────────────────────── */

export const DASHBOARD_TABS = [
  { key: 'today', i18n: 'dashboard.tab.today', asks: 'dispatcher' },
  { key: 'ontime', i18n: 'dashboard.tab.ontime', asks: 'ops_manager' },
  { key: 'fleet', i18n: 'dashboard.tab.fleet', asks: 'fleet_manager' },
  { key: 'contract', i18n: 'dashboard.tab.contract', asks: 'owner' },
  { key: 'people', i18n: 'dashboard.tab.people', asks: 'hr' },
] as const;

export type DashboardTab = (typeof DASHBOARD_TABS)[number]['key'];

/** แท็บเริ่มต้นของทุกบทบาท */
export const DEFAULT_TAB: DashboardTab = 'today';

/* ─── รูปที่ช่องใช้วาด ─────────────────────────────────────────────── */

export type TileShape =
  | 'number' // ตัวเลขใหญ่ + บรรทัดท้าย
  | 'split' // แท่ง 100% หลายส่วน (ป้ายอยู่ในแท่ง)
  | 'ranking' // แท่งนอนเรียง
  | 'list' // ลิสต์แถว
  | 'day_shape'; // ไทม์ไลน์รายชั่วโมง

/* ─── คู่เทียบ (ADR 0365 D3) ───────────────────────────────────────── */

/**
 * **เซิร์ฟเวอร์เป็นคนบอกว่าช่องนี้เทียบกับอะไร** ไม่ใช่จอเดาเอง
 * ไม่งั้นวันหนึ่งสองช่องข้างกันจะเทียบคนละฐานโดยไม่มีใครรู้
 */
export type CompareMode =
  | 'yesterday_now' // เมื่อวาน เวลาเดียวกัน — ช่องที่นับสดของวันนี้
  | 'yesterday_full' // เมื่อวาน ทั้งวัน — ช่องที่นับทั้งวัน
  | 'prev_period' // ช่วงก่อนที่ยาวเท่ากัน — แท็บที่มีช่วงเวลา
  | 'same_day_last_month'; // เดือนก่อน ณ วันเดียวกัน — แท็บ "ณ ตอนนี้"

export interface TileCompare {
  mode: CompareMode;
  prev: number | null;
  dir: 'up' | 'down' | 'flat';
}

/* ─── ช่อง ─────────────────────────────────────────────────────────── */

export interface DashboardTileDef {
  key: string;
  tab: DashboardTab;
  /** ลำดับแถบในแท็บ — 0 คือแถบแรก */
  band: number;
  /**
   * จำนวนคอลัมน์ของแถบ — **ไม่เท่ากับจำนวนการ์ดเสมอไป**
   * แถบ "รถ" มี 2 ใบแต่เป็น 3 คอลัมน์: ช่องท้ายปล่อยว่างไว้ตั้งใจ เพื่อให้การ์ด
   * ทุกแถบกว้างเท่ากันและขอบซ้าย-ขวาตรงกันทั้งหน้า
   * ถ้าเดาจากจำนวนการ์ด การ์ดจะยืดเต็มแถวแล้วหน้าเบี้ยวทันที (เคยพลาดมาแล้ว)
   */
  cols: number;
  /** คีย์ i18n ของหัวแถบ — null = แถบไม่มีหัว */
  bandHead: string | null;
  /** คีย์ i18n ของ "คำถาม" ที่ขึ้นจริงบนหน้า — ห้ามเป็นข้อความตรง ๆ */
  i18n: string;
  shape: TileShape;
  unit: 'ticket' | 'vehicle' | 'person' | 'contract' | 'reminder' | 'round' | 'thb' | 'km_per_l' | 'none';
  /**
   * ลิงก์ไปหน้าที่ลงมือต่อได้ — `null` = ไม่มีที่ให้ไป และจะไม่มีลูกศร ↗
   *
   * ⚠️ **ห้ามใส่ `/dashboard` นำหน้า** — `app/(dashboard)/` เป็น *route group* ของ Next
   *    วงเล็บไม่เข้า URL · เส้นทางจริงคือ `/tracking` ไม่ใช่ `/dashboard/tracking`
   *    รอบแรกเขียนผิดทั้งห้าอัน แล้วกดลูกศรได้ 404 ทุกใบ
   *    `apps/web/src/lib/dashboard-links.test.ts` ตรึงไว้แล้ว — พิมพ์ผิดเมื่อไรเทสแดง
   *
   * รองรับโทเคน `{today}` = วันบริการวันนี้ (`YYYY-MM-DD`) — หน้าจอแทนค่าให้ตอนเรนเดอร์
   * เพราะแค็ตตาล็อกเป็นค่าคงที่ จะฝังวันที่ตายตัวไม่ได้ · ดู `resolveHref()` ฝั่งเว็บ
   */
  href: string | null;
  /** service เจ้าของตัวเลข — เอกสารประกอบ ใช้ตอนรีวิวว่ามีใครแอบเขียนสูตรที่สอง */
  source: string;
}

export const DASHBOARD_TILES: readonly DashboardTileDef[] = [
  /* ══ แท็บ 1 · วันนี้ ═══════════════════════════════════════════════ */

  {
    key: 'today.timeline',
    tab: 'today', band: 0, cols: 1, bandHead: null,
    i18n: 'dashboard.tile.today.timeline',
    shape: 'day_shape', unit: 'ticket', href: null,
    source: 'TrackingService.getDayShape — สถานะดิบ 7 ค่า (0365 D1)',
  },

  /* ── แถบ "ใบงาน" ── */
  {
    key: 'today.overdue_confirm',
    tab: 'today', band: 1, cols: 3, bandHead: 'dashboard.band.tickets',
    i18n: 'dashboard.tile.today.overdueConfirm',
    shape: 'number', unit: 'ticket', href: '/jobs/tickets?status=assigned&from={today}&to={today}',
    source: 'TrackingService.getStats → exceptions.overdue_confirm',
  },
  {
    key: 'today.overdue_start',
    tab: 'today', band: 1, cols: 3, bandHead: 'dashboard.band.tickets',
    i18n: 'dashboard.tile.today.overdueStart',
    shape: 'number', unit: 'ticket', href: null,
    source: 'TrackingService.getStats → exceptions.overdue_start',
  },
  {
    key: 'today.cancelled',
    tab: 'today', band: 1, cols: 3, bandHead: 'dashboard.band.tickets',
    i18n: 'dashboard.tile.today.cancelled',
    shape: 'number', unit: 'ticket', href: null,
    source: 'TrackingService.getStats → trips.cancelled',
  },

  /* ── แถบ "น้ำมัน" — นับเป็น "ใบเติม" ห้ามอ่านรวมกับใบงาน ── */
  {
    key: 'fuel.logs_today',
    tab: 'today', band: 2, cols: 3, bandHead: 'dashboard.band.fuel',
    i18n: 'dashboard.tile.fuel.logsToday',
    shape: 'number', unit: 'ticket', href: '/fuel',
    source: 'AnalyticsService.getFuelDaily → ใบเติมของวัน (ไม่นับ draft/discarded)',
  },
  {
    key: 'fuel.pending',
    tab: 'today', band: 2, cols: 3, bandHead: 'dashboard.band.fuel',
    i18n: 'dashboard.tile.fuel.pending',
    shape: 'number', unit: 'ticket', href: '/fuel?status=pending',
    source: 'AnalyticsService.getFuelDaily → status=pending + อายุใบเก่าสุด',
  },
  {
    key: 'fuel.reviewed',
    tab: 'today', band: 2, cols: 3, bandHead: 'dashboard.band.fuel',
    i18n: 'dashboard.tile.fuel.reviewed',
    shape: 'number', unit: 'ticket', href: null,
    source: 'AnalyticsService.getFuelDaily → approved + rejected',
  },

  /* ── แถบ "รถ" — นับเป็น "คัน" ห้ามอ่านรวมกับใบงาน ── */
  {
    key: 'today.vehicles_in_use',
    tab: 'today', band: 3, cols: 3, bandHead: 'dashboard.band.vehicles',
    i18n: 'dashboard.tile.today.vehiclesInUse',
    shape: 'number', unit: 'vehicle', href: '/fleet?status=active',
    source: 'TrackingService.getStats → fleet.running + fleet.queued (เจ้าของเคาะ 2026-09-22)',
  },
  {
    key: 'vehicle.docs_expiring',
    tab: 'today', band: 3, cols: 3, bandHead: 'dashboard.band.vehicles',
    i18n: 'dashboard.tile.vehicle.docsExpiring',
    shape: 'number', unit: 'vehicle', href: '/fleet/insurance',
    source: 'ยังไม่ต่อสาย — ขึ้น "—" ตามที่เจ้าของสั่ง (0365) · ปลายทางคือ vehicle.insurance[]/tax_records[]',
  },

  /* ══ แท็บ 2 · ตรงเวลา & งาน ════════════════════════════════════════ */

  {
    key: 'jobs.ontime_split',
    tab: 'ontime', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.jobs.ontimeSplit',
    shape: 'split', unit: 'ticket', href: null,
    source: 'JobsService.getTicketStats (onTimeStages — ห้ามคำนวณใหม่)',
  },
  {
    key: 'jobs.late_by_route',
    tab: 'ontime', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.jobs.lateByRoute',
    shape: 'ranking', unit: 'ticket', href: null,
    source: 'JobsService.getLateByRoute (ต่อยอด onTimeStages + group route_code)',
  },
  {
    key: 'jobs.completed_via',
    tab: 'ontime', band: 1, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.jobs.completedVia',
    shape: 'number', unit: 'ticket', href: null,
    source: 'JobsService.getCompletedVia → admin (ใบก่อนมีฟิลด์ = ถัง "ไม่ระบุ")',
  },
  {
    key: 'jobs.cancelled',
    tab: 'ontime', band: 1, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.jobs.cancelled',
    shape: 'number', unit: 'ticket', href: null,
    source: 'JobsService.getCancelled ตามช่วง + cancel_source',
  },
  // 'jobs.pay_undecided' ถอดแล้ว 2026-10-11 — การตัดสินจ่ายเที่ยวไม่จบเลิกใช้ เงินจบที่พีระมิดการเงินเท่านั้น

  /* ══ แท็บ 3 · รถ & น้ำมัน ══════════════════════════════════════════ */

  {
    key: 'fleet.status_split',
    tab: 'fleet', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.fleet.statusSplit',
    shape: 'list', unit: 'vehicle', href: null,
    source: 'AnalyticsService.getFleetUtilization (ต้องมีทุกสถานะ รวม decommissioned)',
  },
  {
    key: 'fuel.worst_vehicles',
    tab: 'fleet', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.fuel.worstVehicles',
    shape: 'ranking', unit: 'km_per_l', href: null,
    source: 'AnalyticsService.getFuelEfficiency → vehicles[] (km_per_liter === null ไม่เข้าอันดับ)',
  },
  {
    key: 'fuel.kmpl_fleet',
    tab: 'fleet', band: 1, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.fuel.kmplFleet',
    shape: 'number', unit: 'km_per_l', href: null,
    source: 'AnalyticsService.getFuelEfficiency → avg_km_per_liter + measured_vehicles',
  },
  {
    key: 'fuel.cost',
    tab: 'fleet', band: 1, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.fuel.cost',
    shape: 'number', unit: 'thb', href: null,
    source: 'AnalyticsService.getFuelEfficiency → total_cost_thb (เฉพาะใบ approved)',
  },

  /* ══ แท็บ 4 · สัญญา ════════════════════════════════════════════════ */

  {
    key: 'contracts.expiring',
    tab: 'contract', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.contracts.expiring',
    shape: 'list', unit: 'contract', href: null,
    source: 'ContractsService.getStats → expiring_soon + ลิสต์จากลิสต์เดิม',
  },
  {
    key: 'contracts.reminders_due',
    tab: 'contract', band: 0, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.contracts.remindersDue',
    shape: 'list', unit: 'reminder', href: null,
    source: 'ContractRemindersService.summarizeTenant (หน้าต่าง 7 วันชุดเดียวกับ summarize)',
  },
  {
    // plan/finance-full H7 — the money side of the owner's tab: rounds frozen and waiting for approval,
    // and approved rounds not marked paid yet
    key: 'finance.rounds_waiting',
    tab: 'contract', band: 1, cols: 2, bandHead: null,
    i18n: 'dashboard.tile.finance.roundsWaiting',
    shape: 'list', unit: 'round', href: null,
    source: 'pay_batches — status frozen (รออนุมัติ) + approved (รอจ่าย), ยอดจาก snapshot.total',
  },

  /* ══ แท็บ 5 · คน ═══════════════════════════════════════════════════ */

  {
    key: 'people.leave_pending',
    tab: 'people', band: 0, cols: 4, bandHead: null,
    i18n: 'dashboard.tile.people.leavePending',
    shape: 'number', unit: 'ticket', href: null,
    source: 'HrService.getLeaveStats → pending',
  },
  {
    key: 'people.leave_today',
    tab: 'people', band: 0, cols: 4, bandHead: null,
    i18n: 'dashboard.tile.people.leaveToday',
    shape: 'number', unit: 'person', href: null,
    source: 'HrService.getLeaveStats → on_leave_today',
  },
  {
    key: 'people.docs_expiring',
    tab: 'people', band: 0, cols: 4, bandHead: null,
    i18n: 'dashboard.tile.people.docsExpiring',
    shape: 'number', unit: 'person', href: null,
    source: 'HrService.getDocumentStats → expiring + expired',
  },
  {
    key: 'people.headcount',
    tab: 'people', band: 0, cols: 4, bandHead: null,
    i18n: 'dashboard.tile.people.headcount',
    shape: 'number', unit: 'person', href: null,
    source: 'HrService.getEmployeeStats → active + drivers',
  },
] as const;

export type TileKey = (typeof DASHBOARD_TILES)[number]['key'];

/* ─── ตัวช่วย ──────────────────────────────────────────────────────── */

const BY_KEY = new Map(DASHBOARD_TILES.map((t) => [t.key, t]));

export function isTileKey(key: string): boolean {
  return BY_KEY.has(key);
}

export function tileDef(key: string): DashboardTileDef | undefined {
  return BY_KEY.get(key);
}

export function tilesForTab(tab: DashboardTab): DashboardTileDef[] {
  return DASHBOARD_TILES.filter((t) => t.tab === tab);
}

export interface DashboardBand {
  index: number;
  head: string | null;
  /** จำนวนคอลัมน์ — มาจากแค็ตตาล็อก ไม่ใช่ `tiles.length` (ดูหมายเหตุที่ `cols`) */
  cols: number;
  tiles: DashboardTileDef[];
}

/** แถบของแท็บ เรียงตามลำดับที่ต้องขึ้นจอ — ลำดับการ์ดในแถบคือลำดับในแค็ตตาล็อก */
export function bandsForTab(tab: DashboardTab): DashboardBand[] {
  const out: DashboardBand[] = [];
  for (const t of tilesForTab(tab)) {
    let band = out.find((b) => b.index === t.band);
    if (!band) {
      band = { index: t.band, head: t.bandHead, cols: t.cols, tiles: [] };
      out.push(band);
    }
    band.tiles.push(t);
  }
  return out.sort((a, b) => a.index - b.index);
}

/* ─── สถานะของค่าในช่อง ────────────────────────────────────────────── */

export interface TilePayload {
  key: string;
  /** null เมื่อคำนวณไม่ได้ — ต้องมาคู่กับ `cannot_compute` เสมอ */
  value: number | null;
  detail?: unknown;
  compare: TileCompare | null;
  /** คีย์ i18n ของเหตุผล — null เมื่อคำนวณได้ */
  cannot_compute: string | null;
}

/** ด่านความสอดคล้องของ payload หนึ่งช่อง — คืนรายการปัญหา (ว่าง = ผ่าน) */
export function validateTilePayload(p: TilePayload): string[] {
  const problems: string[] = [];
  if (!isTileKey(p.key)) problems.push(`คีย์ "${p.key}" ไม่อยู่ในแค็ตตาล็อก`);
  if (p.value === null && p.cannot_compute === null) {
    problems.push(`${p.key}: value เป็น null แต่ไม่มี cannot_compute`);
  }
  if (p.value !== null && p.cannot_compute !== null) {
    problems.push(`${p.key}: มีทั้ง value และ cannot_compute พร้อมกัน`);
  }
  return problems;
}
